import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { activityService } from '../../../services/activityService';
import { X, Clock, MapPin, Globe } from 'lucide-react';

export default function LoginHistoryModal({ isOpen, onClose, userId }) {
  const [history, setHistory] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (isOpen && userId) {
      loadHistory();
    }
  }, [isOpen, userId, page]);

  const [error, setError] = useState(null);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error, count } = await supabase
        .from('user_login_sessions')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .or('session_type.eq.Login,session_type.eq.SignIn')
        .order('login_time', { ascending: false })
        .range((page - 1) * itemsPerPage, page * itemsPerPage - 1);

      if (error) {
        console.error('Supabase Error:', error);
        setError(error.message || 'Failed to load history');
        throw error;
      }
      
      setHistory(data || []);
      if (count !== null) setTotalCount(count);
    } catch (err) {
      console.error('Error fetching login history:', err);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-background w-full max-w-3xl rounded-lg shadow-lg overflow-hidden border border-border flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <h3 className="text-lg font-semibold text-foreground">Login History</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-0 overflow-auto flex-1">
          {error && (
            <div className="p-4 bg-destructive/10 text-destructive text-sm border-b border-destructive/20">
              Error loading history: {error}
            </div>
          )}
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-12 bg-muted/20 animate-pulse rounded" />
              ))}
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock size={32} className="mx-auto mb-2 opacity-50" />
              <p>No login history found.</p>
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="bg-muted/50 text-muted-foreground border-b border-border">
                <tr>
                  <th className="px-4 py-3 font-medium">Date & Time</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">Location</th>
                  <th className="px-4 py-3 font-medium">Device</th>
                  <th className="px-4 py-3 font-medium">Browser</th>
                  <th className="px-4 py-3 font-medium">IP Address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {history.map((entry, index) => (
                  <tr key={entry.id || index} className="hover:bg-muted/10">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-muted-foreground" />
                        {entry.login_time ? new Date(entry.login_time).toLocaleString() : (entry.created_at ? new Date(entry.created_at).toLocaleString() : 'Time N/A')}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
                        {entry.session_type || (entry.session_activity?.startsWith('Login') ? 'Login' : 'Page View')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-muted-foreground" />
                        {entry.location || entry.city || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-4 py-3">{entry.os_type || entry.os || 'Unknown OS'}</td>
                    <td className="px-4 py-3">{entry.browser_type || entry.browser || 'Unknown Browser'}</td>
                    <td className="px-4 py-3 font-mono text-xs">{entry.ip_address || entry.ip || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-border flex justify-between items-center bg-muted/5">
          <div className="flex gap-2">
            <button 
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="px-3 py-1 text-sm disabled:opacity-50 hover:bg-muted rounded"
            >
                First
            </button>
            <button 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm disabled:opacity-50 hover:bg-muted rounded"
            >
                Previous
            </button>
          </div>
          
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages || 1}
          </span>
          
          <div className="flex gap-2">
            <button 
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm disabled:opacity-50 hover:bg-muted rounded"
            >
                Next
            </button>
            <button 
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm disabled:opacity-50 hover:bg-muted rounded"
            >
                Last
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
